/**
 * Node Type API Client
 * Handles all API calls related to node types catalog
 */

import { NodeType, NodeCategory } from '@flow-studio/types';
import { BaseApiClient } from './baseApiClient';

// Session-level cache: node type code → NodeType (shared across all instances)
const nodeTypeCache = new Map<string, NodeType>();

export class NodeTypeApiClient extends BaseApiClient {
  constructor() {
    super();
  }

  static clearCache(): void {
    nodeTypeCache.clear();
  }

  /**
   * Get all available node types from the backend catalog.
   * Calls: POST /api/v1/process/process-element-types/list
   */
  async getNodeTypes(): Promise<NodeType[]> {
    const response = await this.postQuery<any>(
      '/api/v1/process/process-element-types/list',
      { pageNumber: 1, pageSize: 500 }
    );

    if (!response.success || !response.data) {
      return [];
    }

    const items: any[] = Array.isArray(response.data) ? response.data : [];

    return items.map((et: any): NodeType => ({
      code: et.Code ?? et.code ?? '',
      displayName: et.DisplayName ?? et.Name ?? et.code ?? '',
      description: et.Description ?? et.description ?? '',
      category: et.Category ?? et.category ?? 'default',
      id: et.ProcessElementTypeID ?? et.processElementTypeID,
      iconUrl: et.IconUrl ?? et.iconUrl,
      color: et.Color ?? et.color,
      version: et.Version ?? et.version,
      isAsync: et.IsAsync ?? et.isAsync ?? false,
      supportsMultipleOutputs: et.SupportsMultipleOutputs ?? et.supportsMultipleOutputs ?? false,
      requiresCredentials: et.RequiresCredentials ?? et.requiresCredentials ?? false,
      defaultInputPorts: [],
      defaultOutputPorts: [],
    } as NodeType));
  }

  /**
   * Get all node categories from the backend catalog.
   * Calls: POST /api/v1/process/process-element-categories/list
   */
  async getNodeCategories(): Promise<NodeCategory[]> {
    const response = await this.postQuery<any>(
      '/api/v1/process/process-element-categories/list',
      { pageNumber: 1, pageSize: 200 }
    );

    if (!response.success || !response.data) {
      return [];
    }

    const items: any[] = Array.isArray(response.data) ? response.data : [];

    return items.map((cat: any): NodeCategory => ({
      code: cat.Code ?? cat.code ?? '',
      displayName: cat.Name ?? cat.DisplayName ?? cat.code ?? '',
      description: cat.Description ?? cat.description,
      iconUrl: cat.IconUrl ?? cat.iconUrl,
      color: cat.Color ?? cat.color,
      sortOrder: cat.DisplayOrder ?? cat.displayOrder,
    } as NodeCategory));
  }

  /**
   * Get node type by code
   * Calls backend API: POST /api/v1/process/process-element-types/by-code
   *
   * @param code - The node type code (portable identifier, e.g., "http-request", "manual-trigger")
   * @returns NodeType object with both code (portable) and id (database-specific)
   * @throws Error if the code is not found in the database
   *
   * IMPORTANT: The 'code' field is the portable identifier used across all environments.
   * The 'id' field is the database-specific ProcessElementTypeID (environment-dependent).
   * Always use 'code' for identifying node types to ensure portability.
   */
  async getNodeType(code: string): Promise<NodeType> {
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      throw new Error('Node type code is required');
    }

    // Return cached result if available — avoids redundant API calls during batch saves
    if (nodeTypeCache.has(trimmedCode)) {
      return nodeTypeCache.get(trimmedCode)!;
    }

    try {
      const response = await this.postQuery<any>(
        '/api/v1/process/process-element-types/by-code',
        {
          code: trimmedCode,
          pageNumber: 1,
          pageSize: 1,
        }
      );

      if (!response.success || !response.data || !Array.isArray(response.data) || response.data.length === 0) {
        throw new Error(`Node type not found for code: ${trimmedCode}`);
      }

      const elementType = response.data[0];

      const nodeType: NodeType = {
        code: elementType.Code || elementType.code,
        displayName: elementType.Name || elementType.DisplayName || elementType.code,
        description: elementType.Description || elementType.description || '',
        category: elementType.Category || elementType.category || 'default',
        id: elementType.ProcessElementTypeID ?? elementType.processElementTypeID,
        iconUrl: elementType.IconUrl || elementType.iconUrl,
        color: elementType.Color || elementType.color,
      } as NodeType;

      nodeTypeCache.set(trimmedCode, nodeType);
      return nodeType;
    } catch (error) {
      console.error(`Error fetching node type for code "${trimmedCode}":`, error);
      throw error;
    }
  }
}
